import { createTheme, rem } from '@mantine/core'
import type { MantineColorsTuple, MantineThemeOverride } from '@mantine/core'

// 公開ページ用 theme。追従元 bar-period.com の design token をピクセル準拠で踏襲する。
//   --bg-color: #1c1c21 (チャコール) / --text-sub: #b6b6b6
//   --accent-gold: #C5A059 / --accent-gold-light: #E5C585
//   --font-head: 'Cinzel' (見出し/英字) / --font-body: 'Noto Serif JP' (本文)
// Home / Blog / Menu などで使用 (常時 dark scheme)。

// accent-gold (#C5A059) を中心に組んだ 10 階調。primaryShade(dark) を 5 に固定し、
// index 5 がブランドゴールド、index 3 付近が accent-gold-light (#E5C585) に対応する。
const gold: MantineColorsTuple = [
  '#f8f2e6',
  '#ece0c6',
  '#e0cda2',
  '#E5C585',
  '#cca860',
  '#C5A059',
  '#b08d4b',
  '#8c6f3b',
  '#69532c',
  '#46381d',
]

// 既定の dark を #1c1c21 基調へ寄せる。dark.7 を body 背景 (#1c1c21) に合わせ、
// より暗い面 (footer/overlay 等) は dark.8/9 を使う。
const charcoal: MantineColorsTuple = [
  '#c9c9cf',
  '#b6b6bd',
  '#828289',
  '#696970',
  '#42424a',
  '#34343b',
  '#26262c',
  '#1c1c21',
  '#161619',
  '#0f0f12',
]

export const publicTheme = createTheme({
  primaryColor: 'gold',
  primaryShade: { light: 5, dark: 5 },
  colors: { gold, dark: charcoal },
  fontFamily: '"Noto Serif JP", serif',
  // Cinzel は Latin のみ。日本語は "Noto Serif JP" にフォールバックさせる。
  headings: {
    fontFamily: 'Cinzel, "Noto Serif JP", serif',
    fontWeight: '400',
    sizes: {
      h1: { fontSize: rem(34), lineHeight: '1.3' },
      h2: { fontSize: rem(40), lineHeight: '1.2' },
      h3: { fontSize: rem(20), lineHeight: '1.4' },
    },
  },
  defaultRadius: '0',
})

// 管理画面 / 認証ページは業務 UI の視認性・操作性を優先し、Mantine デフォルトを
// そのまま使う。MantineProvider#theme に undefined を渡すと Mantine がデフォルト
// theme を内部で使うため、専用の空 theme オブジェクトを用意する必要はない。
// `themeFor()` は admin 側で theme: undefined を返す。

// 戻り値オブジェクトを毎呼び出し新規生成すると、参照変化で MantineProvider の
// useMemo(mergeMantineTheme, [theme]) や React の prop diff が無駄に miss する。
// 2 つのモード分の constant を予め束ねて、関数は constant を選ぶだけにする。
const PUBLIC_THEME_CONFIG = {
  theme: publicTheme as MantineThemeOverride | undefined,
  forceColorScheme: 'dark',
} as const

const ADMIN_THEME_CONFIG = {
  theme: undefined as MantineThemeOverride | undefined,
  forceColorScheme: 'light',
} as const

// Inertia の現在の page component 名から theme と colorScheme を決定する。
// Admin/* および Auth/* は管理画面扱い (Mantine デフォルト + light)、それ以外は
// 公開扱い (publicTheme + dark)。entrypoint と SSR の両方で参照される。
export function themeFor(component: string): typeof PUBLIC_THEME_CONFIG | typeof ADMIN_THEME_CONFIG {
  const isAdmin = component.startsWith('Admin/') || component.startsWith('Auth/')
  return isAdmin ? ADMIN_THEME_CONFIG : PUBLIC_THEME_CONFIG
}
